/**
 * CallRail Safe-Fetch Wrapper (Story 22.1, AC #5, AC #7)
 *
 * Thin shim over the existing `safeFetch`. The shim's job is THREE-fold:
 *
 *  1. Inject the `Authorization: Token token="<decryptedToken>"` header
 *     (CallRail v3 auth shape per
 *     `support.callrail.com/hc/en-us/articles/5711821845389-CallRail-s-API`
 *     verified 2026-05-14) on every request. CallRail uses a NON-STANDARD
 *     Authorization value: literally `Token token="..."` with the word
 *     "Token" followed by a space, then `token="..."`. NOT `Bearer`.
 *  2. Redact the literal token value from the error message before any
 *     structured-log emission, Sentry breadcrumb, or adapter-error surface
 *     (Round 3 Red Team Attack #3). Targets both the raw token AND the
 *     `Token token="..."` header pattern AND the URL-encoded variant.
 *  3. On HTTP 429, apply a 60-second cooldown per AC #7 — CallRail does
 *     NOT document `Retry-After` or `X-RateLimit-*` headers (verified
 *     `support.callrail.com/hc/en-us/articles/12132320044173-HTTP-response-codes`
 *     2026-05-14 is silent on this), so the adapter falls back to a
 *     conservative wait.
 *
 * **Body sanity check (defense — Code Review Pass 3 invariant carried
 * forward from Semrush 20.4 + Moz 20.7):** the request body MUST NOT
 * contain the API token. CallRail sends the token via the Authorization
 * header ONLY; if a future helper accidentally stuffs the token into the
 * body (where it doesn't belong), this synchronous TypeError catches the
 * misuse before the request goes out.
 *
 * **Round 3 Red Team Attack #3:** without explicit sanitization at the
 * boundary, the token can leak into:
 *  - Cloudflare Workers' built-in request logs (URL is fine; headers are
 *    not logged by default but Sentry/structured-log integrations often
 *    include request context)
 *  - Sentry breadcrumbs / error-tracking stacks
 *  - Our own structured-log events (`callrail_api_call_*`)
 *  - Customer-visible adapter error messages on a 5xx surface (where the
 *    upstream provider may echo the request headers in the body)
 *
 * Defense at the boundary > defense at every consumer.
 */

import { safeFetch } from "../safe-fetch";
import type { AdapterResult } from "../adapter";
import { sanitizeCallRailErrorMessage } from "./errors";

/** Per-call timeout (15s — matches Ahrefs / Semrush / Moz). */
const CALLRAIL_TIMEOUT_MS = 15_000;

/**
 * 429 fallback cooldown (60 seconds). CallRail's documented quotas
 * (1K/hr = ~1 req every 3.6 sec) mean a 60-sec cooldown drops the rate
 * to ~1/min and lets the bucket refill safely. The docs are silent on
 * `Retry-After`, so we use this static fallback.
 */
export const CALLRAIL_429_COOLDOWN_MS = 60_000;

export interface SafeFetchCallRailOptions {
  /** Optional integrationId for log context only — no per-integration cooldown tracker (unlike Moz). */
  readonly integrationId?: string;
  /** Optional override for the wall-clock (for test determinism). */
  readonly now?: () => number;
}

/**
 * Build the `Authorization: Token token="..."` header value. Exported so
 * tests can assert the literal shape against the docs.
 *
 * **Critical formatting:** the literal is `Token token="<KEY>"` — with a
 * single space between "Token" and "token", an equals sign, and DOUBLE
 * QUOTES around the key value. CallRail's parser does NOT accept variants
 * (no `Bearer <KEY>`, no `Token <KEY>`, no `token=<KEY>` without quotes).
 *
 * The double quotes are part of the auth value — they ARE sent to CallRail
 * inside the Authorization header. This is genuinely the documented
 * format (the helper-center article AND apidocs.callrail.com both quote
 * it verbatim).
 */
export function buildCallRailAuthHeader(decryptedToken: string): string {
  return `Token token="${decryptedToken}"`;
}

/**
 * `safeFetch` wrapper that:
 *
 *  1. Injects the `Authorization: Token token="<decryptedToken>"` header
 *     alongside any caller-provided headers.
 *  2. On HTTP 429 from CallRail, sleeps for `CALLRAIL_429_COOLDOWN_MS`
 *     before returning the error result (so the surrounding queue's
 *     exponential backoff doesn't immediately fire another request).
 *  3. On error, sanitizes the error message via
 *     `sanitizeCallRailErrorMessage` so the literal token is redacted
 *     from any surface that downstream code might log.
 *
 * @param url - The full CallRail v3 URL (e.g. `https://api.callrail.com/v3/a.json`).
 * @param decryptedToken - The plaintext API token. In scope for the lifetime
 *   of this call ONLY.
 * @param init - Standard `RequestInit` for the underlying fetch.
 * @param options - Optional integration context for log enrichment.
 */
export async function safeFetchCallRail(
  url: string,
  decryptedToken: string,
  init?: RequestInit,
  _options: SafeFetchCallRailOptions = {},
): Promise<AdapterResult<Response>> {
  // Pass 3 defense — synchronous TypeError if a caller accidentally puts
  // the API token in the body. Cheap check; only trips on actual misuse.
  // Threshold 20 chars matches the Zod connect-schema lower bound; below
  // 20 we skip (too-short test stubs would false-positive on shared substrings).
  if (
    decryptedToken.length >= 20 &&
    typeof init?.body === "string" &&
    init.body.includes(decryptedToken)
  ) {
    throw new TypeError(
      "safeFetchCallRail: init.body must not contain the API token. Use the Authorization: Token token=\"...\" header only.",
    );
  }

  // Inject `Authorization: Token token="..."` header; preserve any
  // caller-provided headers (e.g. Accept).
  //
  // Pass 1 review fix: previously used a plain Record<string, string>
  // and `Object.assign`, which is case-SENSITIVE. A caller passing
  // `{ authorization: "Token token=stale" }` (lowercase `a`) would
  // produce a merged object containing BOTH `authorization` and
  // `Authorization` keys — depending on how the underlying fetch
  // serialized headers, the stale lowercase value could win. Using
  // the Headers class enforces HTTP-standard case-insensitive key
  // semantics (RFC 7230 §3.2) so there is exactly one Authorization
  // value seen by the upstream provider.
  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((value, key) => headers.set(key, value));
    } else if (Array.isArray(init.headers)) {
      for (const [key, value] of init.headers) {
        headers.set(key, value);
      }
    } else {
      for (const [key, value] of Object.entries(init.headers)) {
        if (typeof value === "string") headers.set(key, value);
      }
    }
  }
  // CRITICAL: set Authorization AFTER any caller-provided headers so
  // the helper's value always wins (defense against a misconfigured
  // caller passing a stale token in their headers map).
  headers.set("Authorization", buildCallRailAuthHeader(decryptedToken));

  const result = await safeFetch(
    url,
    { ...init, headers },
    CALLRAIL_TIMEOUT_MS,
  );

  // Pass 1 review fix: the previous implementation called
  // `setTimeout(..., 60_000)` inline here on 429. That blocks the
  // calling Worker request for 60 seconds, exceeding both CPU-time and
  // wall-clock budgets on Cloudflare Workers AND hanging interactive
  // UI calls (probe / fetchMetrics on dashboard load). The cooldown
  // belongs in the caller's retry-scheduling logic — surface the 429
  // verbatim and let the queue's retry backoff handle the delay.
  //
  // The retryable=true flag on AdapterError + the queue's
  // exponential-backoff retry policy together implement the documented
  // 60-sec-cooldown intent without blocking a single request handler.

  if (result.success) {
    return result;
  }

  // Error path — re-sanitize via CallRail-specific helper.
  return {
    success: false,
    error: {
      ...result.error,
      message: sanitizeCallRailErrorMessage(
        result.error.message,
        decryptedToken,
      ),
      providerMessage: result.error.providerMessage
        ? sanitizeCallRailErrorMessage(
            result.error.providerMessage,
            decryptedToken,
          )
        : undefined,
    },
  };
}
